// DRASTIC_CHANGE commit orchestration for product refresh (KAN-1464).
//
// Extracted from UpdateProductModal.tsx's Drastic Change dialog "Yes" handler:
// - startDrasticChange mirrors the CREATE_NEW_PRODUCT apply call and the
//   conditional rename of the newly created project (~lines 2154-2199).
// - runDrasticBuild mirrors the run-job start, poll-to-terminal loop, and the
//   feature import step (~lines 2199-2305), preserving terminal conditions,
//   progress phases, and error semantics exactly.
//
// Behavioral contract (locked): the ORIGINAL product is never written to by
// this path. applySpecificationChanges(CREATE_NEW_PRODUCT) is scoped to the
// original project id but only *reads* it to derive a new project on the
// WorkProductProcessor side; updateProjectApi/run-job/import all target the
// newly created project id. Unlike commit.ts's staged-mode functions, this
// module preserves the modal's "non-fatal, keep going" semantics for the
// rename step and the feature-import step (the modal only console.errors and
// continues for both), since that is the exact behavior being extracted.
// Job start/poll failures and FAILED terminal status DO reject, mirroring the
// modal's setBuildJobError branches.

import { applySpecificationChanges, importFeaturesFromPreprocessing, updateProjectApi } from "@/api/api";
import { getJobStatus, submitJobToProcessor } from "@/api/workProcessor";

const BUILD_POLL_INTERVAL_MS = 2000;
const FEATURES_SECTION_ID = [232, 238, 237];
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

export interface DrasticStartResult {
    newProjectId: number;
    jobId: string | null;
    runJobParams: unknown | null;
}

export interface StartDrasticChangeArgs {
    token: string;
    projectId: number;
    userId: number;
    newProductName: string;
    comparisonResult: unknown;
    originalAnswers: unknown[];
}

/**
 * Apply a CREATE_NEW_PRODUCT specification change and, if the backend
 * requires a follow-up build job, rename the freshly created project.
 *
 * Mirrors UpdateProductModal.tsx:2154-2199. The `requires_job_run` branch
 * (2172-2197) additionally renames the new project via updateProjectApi;
 * that rename failure is non-fatal in the modal (caught, logged, continues)
 * so it stays non-fatal here too. When `requires_job_run` is falsy (or
 * `run_job_params` is missing — the modal's `&&` guard at line 2172),
 * `jobId`/`runJobParams` resolve to null and the caller's `runDrasticBuild`
 * call becomes a no-op.
 */
export async function startDrasticChange(args: StartDrasticChangeArgs): Promise<DrasticStartResult> {
    const { token, projectId, userId, newProductName, comparisonResult, originalAnswers } = args;

    const applyData = await applySpecificationChanges(
        {
            projectid: projectId,
            userid: userId,
            action: "CREATE_NEW_PRODUCT",
            new_project_name: newProductName,
            comparison_result: comparisonResult,
            original_answers: originalAnswers
        },
        token
    );

    const result = applyData.result as
        | { requires_job_run?: boolean; run_job_params?: unknown; new_project_id?: number }
        | undefined;

    const newProjectId = result?.new_project_id as number;

    if (result?.requires_job_run && result?.run_job_params) {
        try {
            await updateProjectApi(
                {
                    project_id: newProjectId,
                    project_name: newProductName,
                    project_description: newProductName,
                    status: "Active"
                },
                token
            );
        } catch {
            // Non-fatal - continue with the build (mirrors the modal's catch block).
        }

        return { newProjectId, jobId: null, runJobParams: result.run_job_params };
    }

    // Legacy flow: no job run required.
    return { newProjectId, jobId: null, runJobParams: null };
}

export interface RunDrasticBuildArgs {
    token: string;
    start: DrasticStartResult;
    onProgress?: (phase: string, message?: string) => void;
    pollIntervalMs?: number;
}

/**
 * Start the full-product-build run-job (if one is required) and poll it to a
 * terminal state, then import features/user stories from the pre-processed
 * content.
 *
 * Mirrors UpdateProductModal.tsx:2199-2305:
 * - No-op when `start.runJobParams` is null (legacy/no-job-run flow).
 * - Terminal conditions: status === "COMPLETED" triggers feature import and
 *   resolves; status === "FAILED" rejects with `message ?? error ??
 *   "Build failed"`; anything else (QUEUED/PROCESSING) polls again after
 *   `pollIntervalMs` (default 2000ms, matching the modal's setTimeout).
 * - The feature-import call failure is non-fatal (modal only logs and sets a
 *   degraded message) — runDrasticBuild resolves either way once COMPLETED
 *   is observed.
 * - Transient poll failures (network blips, 5xx) do not immediately reject
 *   the build: a `getJobStatus` throw is caught, reported via
 *   `onProgress?.("building", "Error checking build status")` (message
 *   parity with the modal's pollError catch at UpdateProductModal.tsx:2298-
 *   2301), and retried after the normal poll interval. A successful poll
 *   resets the consecutive-failure counter. Only after
 *   `MAX_CONSECUTIVE_POLL_FAILURES` (3) consecutive failures does
 *   runDrasticBuild reject, with a message including "Error checking build
 *   status" and the last underlying error's message.
 */
export async function runDrasticBuild(args: RunDrasticBuildArgs): Promise<void> {
    const { token, start, onProgress, pollIntervalMs = BUILD_POLL_INTERVAL_MS } = args;

    if (!start.runJobParams) {
        return;
    }

    onProgress?.("setup", "Setting up new product...");
    onProgress?.("starting", "Starting full product build...");

    const { job_id: jobId } = await submitJobToProcessor(start.runJobParams as any, token);

    let consecutiveFailures = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        let status;
        try {
            status = await getJobStatus(jobId, token);
            consecutiveFailures = 0;
        } catch (pollError) {
            consecutiveFailures += 1;

            const lastErrorMessage = pollError instanceof Error ? pollError.message : String(pollError);

            if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
                throw new Error(`Error checking build status: ${lastErrorMessage}`);
            }

            onProgress?.("building", "Error checking build status");
            await sleep(pollIntervalMs);
            continue;
        }

        if (status.status === "COMPLETED") {
            onProgress?.("importing", "Importing features and user stories...");

            try {
                await importFeaturesFromPreprocessing(
                    { projectid: start.newProjectId, sectionid: FEATURES_SECTION_ID },
                    token
                );
            } catch {
                // Non-fatal - feature import failure still counts as a built product.
            }

            onProgress?.("complete", "Product built successfully!");
            return;
        }

        if (status.status === "FAILED") {
            throw new Error(status.message || status.error || "Build failed");
        }

        onProgress?.("building", status.message || "Building product...");

        await sleep(pollIntervalMs);
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}
